// shop-frontend/src/components/ShopAddStory.jsx
// Lets a shop post a 24h story (image/video) to its followers' story rows.
// Posts to /api/stories/shop (shopAuth). Also lists the shop's live stories
// (views / comments) with a delete button.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Loader2, Check, X, Trash2, Eye, MessageCircle } from "lucide-react";
import { request, toAbsUrl } from "../utils/api";

function timeLeft(expiresAt) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!(ms > 0)) return "expired";
  const h = Math.floor(ms / 3600000);
  return h >= 1 ? `${h}h left` : `${Math.max(1, Math.floor(ms / 60000))}m left`;
}

export default function ShopAddStory() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(""); // "", "ok", error message
  const [caption, setCaption] = useState("");
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState([]);
  const [deleting, setDeleting] = useState("");

  const loadMine = useCallback(async () => {
    try {
      const data = await request("/stories/shop/mine", { auth: true });
      setMine(Array.isArray(data?.stories) ? data.stories : []);
    } catch {
      /* the list is optional; posting still works */
    }
  }, []);

  useEffect(() => { loadMine(); }, [loadMine]);

  async function removeStory(id) {
    if (!window.confirm("Delete this story? Shoppers will stop seeing it right away.")) return;
    setDeleting(id);
    try {
      await request(`/stories/shop/${id}`, { method: "DELETE", auth: true });
      setMine((list) => list.filter((s) => s._id !== id));
    } catch (err) {
      setStatus(err?.message || "Could not delete the story");
    } finally {
      setDeleting("");
    }
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setBusy(true);
    setStatus("");
    try {
      const fd = new FormData();
      fd.append("media", file);
      if (caption.trim()) fd.append("caption", caption.trim());
      await request("/stories/shop", { method: "POST", body: fd, auth: true });
      setStatus("ok");
      setCaption("");
      loadMine();
      setTimeout(() => { setStatus(""); setOpen(false); }, 1800);
    } catch (err) {
      setStatus(err?.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "14px 16px", marginBottom: 16, borderRadius: 16,
        border: "1px solid rgba(34,211,238,0.3)", background: "rgba(34,211,238,0.06)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 8, padding: "10px 16px",
          borderRadius: 10, border: "none", cursor: "pointer", fontWeight: 700,
          background: "linear-gradient(135deg,#8b5cf6,#3aa8ff)", color: "#fff",
        }}
      >
        <Plus size={18} /> Add a Story
      </button>
      <span style={{ fontSize: 13, opacity: 0.8 }}>
        Shoppers who follow you will see it for 24 hours — great for a deal or new drop.
      </span>

      {open && (
        <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Optional caption…"
            maxLength={300}
            style={{
              flex: 1, minWidth: 160, padding: "10px 12px", borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "inherit",
            }}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 16px",
              borderRadius: 10, border: "1px solid rgba(255,255,255,0.2)", cursor: busy ? "not-allowed" : "pointer",
              background: "transparent", color: "inherit", fontWeight: 600, whiteSpace: "nowrap",
            }}
          >
            {busy ? <Loader2 size={16} className="spin" /> : status === "ok" ? <Check size={16} /> : null}
            {busy ? "Uploading…" : status === "ok" ? "Posted!" : "Choose photo / video"}
          </button>
        </div>
      )}

      {status && status !== "ok" && (
        <span style={{ width: "100%", display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#f87171" }}>
          <X size={14} /> {status}
        </span>
      )}

      {mine.length > 0 && (
        <div style={{ width: "100%", marginTop: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, opacity: 0.9 }}>
            Your live stories ({mine.length})
          </div>
          <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
            {mine.map((st) => (
              <div
                key={st._id}
                style={{
                  flex: "0 0 auto", width: 112, borderRadius: 12, overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.12)", background: "rgba(0,0,0,0.25)",
                }}
              >
                <div style={{ position: "relative", width: "100%", height: 150, background: "#111" }}>
                  {st.mediaType === "video" ? (
                    <video src={toAbsUrl(st.mediaUrl)} muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <img src={toAbsUrl(st.mediaUrl)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  )}
                  <button
                    type="button"
                    title="Delete story"
                    disabled={deleting === st._id}
                    onClick={() => removeStory(st._id)}
                    style={{
                      position: "absolute", top: 6, right: 6, width: 28, height: 28, borderRadius: 8,
                      border: "none", background: "rgba(0,0,0,0.65)", color: "#f87171",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: deleting === st._id ? "wait" : "pointer",
                    }}
                  >
                    {deleting === st._id ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                  </button>
                  {st.hidden && (
                    <span style={{ position: "absolute", left: 6, bottom: 6, fontSize: 10, padding: "2px 6px", borderRadius: 6, background: "#b91c1c", color: "#fff" }}>
                      Hidden
                    </span>
                  )}
                </div>
                <div style={{ padding: "6px 8px", fontSize: 11, display: "flex", flexDirection: "column", gap: 3 }}>
                  <span style={{ display: "flex", gap: 10, opacity: 0.9 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Eye size={12} /> {st.viewsCount}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 3 }}><MessageCircle size={12} /> {st.commentsCount}</span>
                  </span>
                  <span style={{ opacity: 0.6 }}>{timeLeft(st.expiresAt)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*,video/*" onChange={handleFile} style={{ display: "none" }} />
    </div>
  );
}
